import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API } from '@/lib/apiBase';

axios.defaults.timeout = 10000;

const AuthContext = createContext(null);
const AUTH_MODE = process.env.REACT_APP_AUTH_MODE || 'sso';

// RÈGLE 4: Timeout de session (4h comme la Centrale)
const SESSION_TIMEOUT_MS = 4 * 60 * 60 * 1000; // 4 heures en millisecondes

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);
  const [maintenanceMode, setMaintenanceMode] = useState(null);
  const [permissions, setPermissions] = useState(null);

  // Vérification du timeout de session (sans appeler logout ici : évite closure stale avec useCallback [])
  const checkSessionTimeout = useCallback(() => {
    const sessionStart = localStorage.getItem('sessionStart');
    if (sessionStart) {
      const elapsed = Date.now() - parseInt(sessionStart, 10);
      if (elapsed > SESSION_TIMEOUT_MS) {
        console.log('Session expirée (timeout 4h)');
        localStorage.removeItem('token');
        localStorage.removeItem('sessionStart');
        setToken(null);
        setUser(null);
        setPermissions(null);
        return false;
      }
    }
    return true;
  }, []);

  const fetchMaintenanceStatus = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/maintenance`);
      setMaintenanceMode(response.data);
    } catch (error) {
      console.error('Error fetching maintenance status:', error);
    }
  }, []);

  const fetchPermissions = useCallback(async (authToken) => {
    try {
      const response = await axios.get(`${API}/permissions/me`, {
        headers: { Authorization: `Bearer ${authToken}` }
      });
      setPermissions(response.data.permissions);
    } catch (error) {
      console.error('Error fetching permissions:', error);
      // Default permissions on error
      setPermissions({
        products: 'read',
        categories: 'read',
        entries: 'read',
        costs: 'read',
        refacturation: 'read',
        statistics: 'read',
        users: 'read',
        development: 'read',
      });
    }
  }, []);

  const fetchUser = useCallback(async () => {
    if (!token) {
      setLoading(false);
      return;
    }

    // Vérifier le timeout avant de fetch l'utilisateur
    if (!checkSessionTimeout()) {
      setLoading(false);
      return;
    }

    try {
      const response = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUser(response.data);
      // Fetch permissions after user is loaded
      await fetchPermissions(token);
    } catch (error) {
      console.error('Error fetching user:', error);
      if (error.response?.status === 401 || !error.response) {
        localStorage.removeItem('token');
        localStorage.removeItem('sessionStart');
        setToken(null);
        setUser(null);
        setPermissions(null);
      }
    } finally {
      setLoading(false);
    }
  }, [token, checkSessionTimeout, fetchPermissions]);

  useEffect(() => {
    fetchMaintenanceStatus();
    fetchUser();

    // Filet de sécurité : ne pas laisser l’UI bloquée si /api ne répond jamais (proxy, réseau, etc.)
    const watchdog = setTimeout(() => {
      setLoading(false);
    }, 12000);

    const intervalId = setInterval(() => {
      if (token && !checkSessionTimeout()) {
        // La session a expiré
      }
    }, 60000);

    return () => {
      clearInterval(intervalId);
      clearTimeout(watchdog);
    };
  }, [fetchMaintenanceStatus, fetchUser, token, checkSessionTimeout]);

  // Login classique (mode local) — prysma_bdd.users : username + password_hash
  const login = async (username, password) => {
    const response = await axios.post(
      `${API}/auth/login`,
      { username, password },
      { headers: { 'Content-Type': 'application/json' } }
    );
    const { token: newToken, user: userData } = response.data;
    
    // Stocker le token et le timestamp de début de session
    localStorage.setItem('token', newToken);
    localStorage.setItem('sessionStart', Date.now().toString());
    setToken(newToken);
    setUser(userData);
    
    // Fetch permissions after login
    await fetchPermissions(newToken);
    
    return userData;
  };

  // Login SSO (mode sso)
  // RÈGLE 2: Le sso_token n'est pas stocké, seulement envoyé au backend
  const loginWithSSO = async (ssoToken) => {
    const response = await axios.post(`${API}/auth/sso`, null, {
      params: { sso_token: ssoToken }
    });
    const { token: newToken, user: userData } = response.data;
    
    // Stocker uniquement le token d'application (pas le sso_token)
    // RÈGLE 4: Démarrer le compteur de session
    localStorage.setItem('token', newToken);
    localStorage.setItem('sessionStart', Date.now().toString());
    setToken(newToken);
    setUser(userData);
    
    // Fetch permissions after SSO login
    await fetchPermissions(newToken);
    
    return userData;
  };

  const logout = useCallback(() => {
    // Nettoyer complètement la session
    localStorage.removeItem('token');
    localStorage.removeItem('sessionStart');
    setToken(null);
    setUser(null);
    setPermissions(null);
  }, []);

  const updateMaintenanceMode = async (isActive, reason = '', estimatedEnd = null) => {
    const response = await axios.put(
      `${API}/maintenance`,
      { is_active: isActive, reason, estimated_end: estimatedEnd },
      { headers: { Authorization: `Bearer ${token}` } }
    );
    setMaintenanceMode(response.data);
    return response.data;
  };

  // Permission helpers
  const hasPermission = (module, requiredLevel = 'read') => {
    // Admin always has full access
    if (user?.role === 'admin') return true;

    // Legacy flag développeur (toggle utilisateurs) — accès complet espace Dev
    if (module === 'development' && user?.is_developer === true) return true;
    
    if (!permissions || !permissions[module]) return false;
    
    const levelHierarchy = { none: 0, read: 1, write: 2 };
    const userLevel = permissions[module] || 'read';
    return levelHierarchy[userLevel] >= levelHierarchy[requiredLevel];
  };

  const canRead = (module) => hasPermission(module, 'read');
  const canWrite = (module) => hasPermission(module, 'write');
  const canAccess = (module) => hasPermission(module, 'read'); // Alias for clarity

  const isAdmin = user?.role === 'admin';
  const isGestionnaire = user?.role === 'gestionnaire' || isAdmin;
  const isDeveloppeur = user?.role === 'developpeur';
  const canEdit = isGestionnaire;
  const isDeveloper = isDeveloppeur || user?.is_developer === true;

  return (
    <AuthContext.Provider value={{
      user,
      token,
      loading,
      maintenanceMode,
      permissions,
      authMode: AUTH_MODE,
      login,
      loginWithSSO,
      logout,
      fetchMaintenanceStatus,
      updateMaintenanceMode,
      isAdmin,
      isGestionnaire,
      isDeveloppeur,
      isDeveloper,
      canEdit,
      hasPermission,
      canRead,
      canWrite,
      canAccess
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
